import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PermissionsContext = createContext(null);

export const PermissionsProvider = ({ children }) => {
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setPermissions(null);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/permissions/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPermissions(response.data);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // Check if user can perform an action on a module
  const can = useCallback((module, action) => {
    if (!permissions) return false;
    
    const modulePerms = permissions[module];
    if (!modulePerms) return false;
    
    return modulePerms[action] === true;
  }, [permissions]);

  // Check if user can view a module
  const canView = useCallback((module) => can(module, 'view'), [can]);
  
  // Check if user can create in a module
  const canCreate = useCallback((module) => can(module, 'create'), [can]);
  
  // Check if user can edit in a module
  const canEdit = useCallback((module) => can(module, 'edit'), [can]);
  
  // Check if user can delete in a module
  const canDelete = useCallback((module) => can(module, 'delete'), [can]);

  // Refresh permissions (call after login or role change)
  const refreshPermissions = useCallback(() => {
    setLoading(true);
    fetchPermissions();
  }, [fetchPermissions]);

  return (
    <PermissionsContext.Provider value={{
      permissions,
      loading,
      can,
      canView,
      canCreate,
      canEdit,
      canDelete,
      refreshPermissions
    }}>
      {children}
    </PermissionsContext.Provider>
  );
};

// Custom hook to use permissions
export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

export default PermissionsContext;
